import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  ApiButton,
  Box,
  Button,
  ContentTemplate,
  Divider,
  Grid,
  KeyValue,
  Pill,
  SectionHeader,
  TitleHeader,
  Typography,
} from 'web-library';
import { makeStyles } from 'tss-react/mui';
import { useHistory, useParams } from 'react-router-dom';
import SubjectTable from '../../../modules/specialConsideration/components/SubjectTable';
import DocumentBadge from '../../../components/DocumentBadge';
import RouteKey from '../../../routes/key';
import { stringToLongDateAndShortTime, stringToSemanticDisplay } from '../../../utils/DateUtils';
import { useGetSCSummary } from '../../../api/candidate/special-consideration/@candidate/queries-services/queries';
import { UploadedFileModel } from '../../../api-refactored/special-consideration/useGetUploadedFileInfo';
import { usePutSpecialConsiderationAmendmentDraft } from '../../../api-refactored/special-consideration/usePutSpecialConsiderationAmendment';
import LoadingComponent from '../../../components/LoadingComponent';
import { useWOGAA } from '../../../hooks/wogaa';

const { specialConsideration, amendSelectSubject } = RouteKey['special-consideration'].child;

// TODO jss-to-tss-react codemod: usages of this hook outside of this file will not be converted.
export const useStyles = makeStyles()((theme) =>
({
  applicationDetailsContainer: {
    marginBottom: theme.spacing(3),
  },
  applicationDetailsTitle: {
    marginTop: theme.spacing(4),
  },
  keyValueContainer: {
    marginTop: theme.spacing(2),
  },
  backButton: {
    marginRight: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      height: 'fit-content',
      marginBottom: theme.spacing(3),
    },
  },
  buttonContainer: {
    marginBottom: theme.spacing(8),
  },
  fileContainer: {
    marginBottom: theme.spacing(7),
  },
  documentBadgeContainer: {
    marginTop: theme.spacing(4),
  },
}));

const ApplicationDetails = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { classes } = useStyles();
  const { uuid: applicationUuid } = useParams<{ uuid: string }>();

  const { data: scSummaryData } = useGetSCSummary(applicationUuid);
 // Wogaa tracking for SC amendment by candidate
  const { transactionStart } = useWOGAA('myexamsseab-6462');

  const newSCAmendmentDraft = usePutSpecialConsiderationAmendmentDraft({
    uuid: applicationUuid,
    onSuccess: () => {
      transactionStart();
      history.push(`${amendSelectSubject.route}`);
    },
  });

  const generateStatusPill = (status: string) => (
    <Grid>
      <Pill label={status} variant="info" />
    </Grid>
  );

  if (!scSummaryData) {
    return <LoadingComponent />;
  }
  const { requestReviewModel, requestSubmittedDataModel, amendable } = scSummaryData;

  return (
    <>
      <TitleHeader
        title={t('specialConsideration->>ApplyForSpecialConsideration.View Application')}
      />
      <ContentTemplate continuous isDividerBottom>
        <SectionHeader
          title={t('specialConsideration->>ApplyForSpecialConsideration.ApplicationDetails')}
          className={classes.applicationDetailsTitle}
        />
        <Grid className={classes.applicationDetailsContainer}>
          <Grid container direction="row">
            <Grid md={6} sm={12} xs={12} className={classes.keyValueContainer}>
              <KeyValue
                id="application-id"
                keyText={t('specialConsideration->>ApplyForSpecialConsideration.ApplicationId')}
                valueText={requestSubmittedDataModel.applicationId}
              />
            </Grid>
            <Grid md={6} sm={12} xs={12} className={classes.keyValueContainer}>
              <KeyValue
                id="status"
                keyText={t('accessArrangement->>Summary.Status')}
                valueText={generateStatusPill(requestSubmittedDataModel.requestStatus)}
              />
            </Grid>
          </Grid>
          <Grid xs={12} className={classes.keyValueContainer}>
            <KeyValue
              id="application-datetime"
              keyText={t('accessArrangement->>Summary.ApplicationDateTime')}
              valueText={stringToLongDateAndShortTime(
                requestSubmittedDataModel.applicationDateTime
              )}
            />
          </Grid>
          {requestSubmittedDataModel.seabRemark && (
            <Grid xs={12} className={classes.keyValueContainer}>
              <KeyValue
                id="seab-remark"
                keyText={t('specialConsideration->>ApplyForSpecialConsideration.Remarks from SEAB')}
                valueText={requestSubmittedDataModel.seabRemark}
              />
            </Grid>
          )}
          {requestSubmittedDataModel.dateToReply && (
            <Grid xs={12} className={classes.keyValueContainer}>
              <KeyValue
                id="date-to-reply"
                keyText={t('specialConsideration->>ApplyForSpecialConsideration.Date to reply by')}
                valueText={stringToSemanticDisplay(requestSubmittedDataModel.dateToReply)}
              />
            </Grid>
          )}
        </Grid>
      </ContentTemplate>
      <ContentTemplate continuous isDividerBottom>
        <Grid className={classes.applicationDetailsContainer}>
          <Typography variant="h3">
            {t('specialConsideration->>ApplyForSpecialConsideration.Candidate Details')}
          </Typography>

          <Grid className={classes.keyValueContainer}>
            <KeyValue
              id="candidateName"
              keyText={t('accessArrangement->>ReviewApplication.Name')}
              valueText={requestReviewModel.candidateBasicInfoModel.statutoryName}
            />
          </Grid>
          <Grid className={classes.keyValueContainer}>
            <KeyValue
              id="candidateNric"
              keyText={t('accessArrangement->>ReviewApplication.NRIC')}
              valueText={requestReviewModel.candidateBasicInfoModel.nricFin}
            />
          </Grid>
        </Grid>
      </ContentTemplate>
      <ContentTemplate type="full" continuous>
        <Grid className={classes.applicationDetailsContainer}>
          <Typography variant="h3">
            {t('specialConsideration->>ApplyForSpecialConsideration.Exam Details')}
          </Typography>

          <Grid className={classes.keyValueContainer}>
            <KeyValue
              id="candidateName"
              keyText={t('accessArrangement->>ApplyForSpecialConsideration.Exam Year')}
              valueText={requestReviewModel.basicExamInfoModel.examYear}
            />
          </Grid>
          <Grid container direction="row">
            <Grid md={6} sm={12} xs={12} className={classes.keyValueContainer}>
              <KeyValue
                id="application-id"
                keyText={t('specialConsideration->>ApplyForSpecialConsideration.ExamLevel')}
                valueText={requestReviewModel.basicExamInfoModel.examLevel}
              />
            </Grid>
            <Grid md={6} sm={12} xs={12} className={classes.keyValueContainer}>
              <KeyValue
                id="candidateName"
                keyText={t('accessArrangement->>ApplyForSpecialConsideration.Exam Series')}
                valueText={requestReviewModel.basicExamInfoModel.examSeries}
              />
            </Grid>
          </Grid>
          <Grid className={classes.keyValueContainer}>
            <KeyValue
              id="candidateNric"
              keyText={t('accessArrangement->>ApplyForSpecialConsideration.Index No.')}
              valueText={requestReviewModel.basicExamInfoModel.indexNo ?? '-'}
            />
          </Grid>
        </Grid>
        <Divider />

        <SubjectTable data={requestReviewModel.selectionModels} />
        <Divider />
        <Typography variant="h3">
          {t('specialConsideration->>ApplyForSpecialConsideration.SupportingDocuments')}
        </Typography>
        <Box className={classes.fileContainer}>
          {requestReviewModel.fileNames
            .filter((item: UploadedFileModel) => item.isSubmitted)
            .map((item: UploadedFileModel) => (
              <DocumentBadge
                key={item.fileUuid}
                className={classes.documentBadgeContainer}
                documentName={item.fileName}
              />
            ))}
        </Box>

        <Box className={classes.buttonContainer}>
          <Button
            id="button-back-to-special-consideration"
            className={classes.backButton}
            variant="outlined"
            onClick={() => history.replace(`/${specialConsideration.route}`)}
          >
            {t('specialConsideration->>ApplyForSpecialConsideration.Back to Special Consideration')}
          </Button>
          <ApiButton
            id="button-amendApplication"
            className={classes.backButton}
            variant="contained"
            status={newSCAmendmentDraft.status}
            disabled={!amendable}
            onClick={() => newSCAmendmentDraft.mutate()}
          >
            {t('specialConsideration->>ApplyForSpecialConsideration.AmendApplication')}
          </ApiButton>
        </Box>
      </ContentTemplate>
    </>
  );
};

export default ApplicationDetails;
